import * as MemoryCache from 'memory-cache';
import addMinutes from 'owa-date-utc-fn/lib/addMinutes';
import addSeconds from 'owa-date-utc-fn/lib/addSeconds';
import getAccessTokenforResourceFromServer, {
    getAccessTokenforResourceFromServerWithRetry,
} from './getAccessTokenforResourceFromServer';
import TokenResponse from 'owa-service/lib/contract/TokenResponse';
import TokenResponseCode from 'owa-service/lib/contract/TokenResponseCode';
import { isFeatureEnabled } from 'owa-feature-flags';
import { enqueue } from './utils/accessTokenForResourceQueue';
import {
    getCachedPromiseForTokenRequest,
    removeCachedPromiseForTokenRequest,
    putPromiseForTokenRequestInCache,
} from './utils/tokenRequestPromiseCache';

const MIN_TIME_TO_EXPIRY_IN_MINUTES = 1;
const TIME_BEFORE_EXPIRY_TO_REFRESH_IN_MILLISECONDS = 2 * 60 * 1000; //try to refresh token 2 minutes before the token expiry
const NUM_OF_RETRIES = 3;

let cache: MemoryCache = null;

export function initializeTokenProvider(cacheObject: MemoryCache): void {
    cache = cacheObject;
}

export async function getAccessTokenforResource(resource: string): Promise<string | undefined> {
    let tokenResponse = getCachedToken(getCache(), resource);

    if (tokenResponse == null) {
        tokenResponse = await fetchAndCacheTokenFromServer(resource);
    }

    return tokenResponse != null ? tokenResponse.AccessToken : undefined;
}

/**
 * Fetches the access token and returns the token immediately if it's cached, or if it's not cached, return the promise
 * that will return the access token
 * @param resource - the resource string
 * @returns [0] - The acccess token if it can be returned synchronously, null otherwise
 * @returns [1] - The promise that will return the access token if it needs to fetched async, null if token is returned synchronously.
 */
export function getAccessTokenforResourceAsLazy(
    resource: string
): [string | undefined, Promise<string | undefined> | undefined] {
    let tokenResponse = getCachedToken(getCache(), resource);

    if (tokenResponse) {
        return [tokenResponse.AccessToken, undefined];
    } else if (isFeatureEnabled('auth-getAccessTokenQueueEnabled')) {
        let cachedPromiseForResource = getCachedPromiseForTokenRequest(resource);
        let promiseForResource;

        if (cachedPromiseForResource) {
            promiseForResource = cachedPromiseForResource;
        } else {
            promiseForResource = enqueue(resource);
            putPromiseForTokenRequestInCache(resource, promiseForResource);
        }

        return [
            undefined,
            promiseForResource.then(value => {
                removeCachedPromiseForTokenRequest(resource);
                return value.AccessToken;
            }),
        ];
    } else {
        return [
            undefined,
            fetchAndCacheTokenFromServer(resource).then((tokenResponse: TokenResponse) => {
                return tokenResponse.AccessToken;
            }),
        ];
    }
}

export async function fetchAndCacheTokenFromServer(resource: string): Promise<TokenResponse> {
    let tokenResponse = isFeatureEnabled('auth-getAccessTokenQueueEnabled')
        ? await getAccessTokenforResourceFromServerWithRetry(resource, NUM_OF_RETRIES)
        : await getAccessTokenforResourceFromServer(resource);

    if (
        tokenResponse != null &&
        tokenResponse.TokenResultCode == TokenResponseCode.Success &&
        tokenResponse.AccessTokenExpiry != null
    ) {
        if (tokenResponse.ExpiresIn && tokenResponse.ExpiresIn > 0) {
            tokenResponse.AccessTokenExpiry = addSeconds(
                Date.now(),
                tokenResponse.ExpiresIn
            ).toISOString();
        }

        const cacheTimeout =
            new Date(tokenResponse.AccessTokenExpiry).getTime() -
            Date.now() -
            TIME_BEFORE_EXPIRY_TO_REFRESH_IN_MILLISECONDS;

        if (cacheTimeout > 0) {
            getCache().put(resource, tokenResponse, cacheTimeout, function(
                key: string,
                value: TokenResponse
            ) {
                fetchAndCacheTokenFromServer(key).catch(() => {
                    // Ignore network errors.  If we fail to refresh the token now, the cache will
                    // be repopulated on demand the next time the token is requested.
                });
            });
        }
    }

    return tokenResponse;
}

export function getCachedToken(cache: MemoryCache, cacheKey: string): TokenResponse | undefined {
    const cachedToken = cache.get(cacheKey) as TokenResponse;
    let tokenResponse: TokenResponse | undefined = undefined;
    if (cachedToken && cachedToken.AccessTokenExpiry) {
        const expiryDateTimeWithMargin = addMinutes(
            new Date(cachedToken.AccessTokenExpiry),
            -MIN_TIME_TO_EXPIRY_IN_MINUTES
        );

        // Check that token is valid for at least 1 minute.
        if (expiryDateTimeWithMargin > new Date(Date.now())) {
            tokenResponse = cachedToken;
        }
    }

    return tokenResponse;
}

function getCache(): MemoryCache {
    if (cache == null) {
        cache = new MemoryCache.Cache();
    }

    return cache;
}
